/**
 * DashboardSharing - Wrapper component for sharing dashboards using ShareModal
 */
import React, { useMemo } from 'react';
import ShareModal, { GroupShareInfo } from 'src/components/ui/ShareModal';
import { ResourcePermission, ResourcePermissionRole, TenantShare } from '../../chat/types';

interface DashboardSharingProps {
  dashboardId: string;
  dashboardName: string;
  userEmail?: string;
  organisationName?: string;
  isOwner?: boolean;
  existingShares?: ResourcePermission[];
  existingGroupShares?: GroupShareInfo[];
  onRemoveGroupShare?: (groupId: string) => Promise<void>;
  tenantShare?: TenantShare | null;
  inheritedTenantPermission?: ResourcePermissionRole | null;
  open: boolean;
  onClose: () => void;
  userPermission: ResourcePermissionRole;
  // Callbacks for individual shares
  onAddPeople?: (
    emailList: string[],
    permission: ResourcePermissionRole,
    message: string,
    groupIds?: string[]
  ) => Promise<void>;
  onRemovePerson?: (permissionId: string) => Promise<void>;
  onUpdatePermission?: (permissionId: string, permission: ResourcePermissionRole) => Promise<void>;
  // Callbacks for tenant sharing
  onShareWithOrganisation?: (permission: ResourcePermissionRole) => Promise<void>;
  onUpdateOrganisationPermission?: (permission: ResourcePermissionRole) => Promise<void>;
  onRemoveOrganisationShare?: () => Promise<void>;
}

const DashboardSharing: React.FC<DashboardSharingProps> = ({
  dashboardName,
  userEmail,
  organisationName,
  isOwner = false,
  open,
  onClose,
  existingShares,
  existingGroupShares,
  onRemoveGroupShare,
  tenantShare,
  inheritedTenantPermission,
  userPermission,
  onAddPeople,
  onRemovePerson,
  onUpdatePermission,
  onShareWithOrganisation,
  onUpdateOrganisationPermission,
  onRemoveOrganisationShare
}) => {
  // Calculate acceptable permissions based on user's current permission level
  const acceptablePermissions = useMemo(() => {
    const basePermissions = [
      ResourcePermissionRole.READ,
      ResourcePermissionRole.COMMENT,
      ResourcePermissionRole.DATA_ENTRY,
      ResourcePermissionRole.WRITE
    ];

    // Users with SHARE permission can grant up to SHARE level
    if (userPermission >= ResourcePermissionRole.SHARE) {
      basePermissions.push(ResourcePermissionRole.SHARE);
    }

    // Only owners can grant OWNER permission
    if (userPermission === ResourcePermissionRole.OWNER) {
      basePermissions.push(ResourcePermissionRole.OWNER);
    }

    return basePermissions;
  }, [userPermission]);

  return (
    <ShareModal
      open={open}
      onClose={onClose}
      title={`Share "${dashboardName}"`}
      user_email={userEmail}
      shareType="dashboard"
      currentShares={existingShares}
      currentGroupShares={existingGroupShares}
      onRemoveGroupShare={onRemoveGroupShare}
      tenantShare={tenantShare}
      inheritedTenantPermission={inheritedTenantPermission}
      organisationName={organisationName}
      isOwner={isOwner}
      currentUserPermission={userPermission}
      acceptablePermissions={acceptablePermissions}
      defaultPermission={ResourcePermissionRole.READ}
      maxExternalPermission={ResourcePermissionRole.READ}
      onAddPeople={onAddPeople}
      onRemovePerson={onRemovePerson}
      onUpdatePermission={onUpdatePermission}
      onShareWithOrganisation={onShareWithOrganisation}
      onUpdateOrganisationPermission={onUpdateOrganisationPermission}
      onRemoveOrganisationShare={onRemoveOrganisationShare}
    />
  );
};

export default DashboardSharing;
